// Controller, provenance and rights are ONE vocabulary across every document
// the app stores — a groove references licensed drum-loop audio just as a
// score carries a transcription credit. The canonical definitions live in the
// storage schema (`documentSchema.ts`) and are re-exported here under their
// original names so this module's consumers are unaffected.
import type { DocumentProvenance, DocumentRights, PrincipalRef } from '@/storage/documentSchema';

export type { PrincipalRef };
export type ScoreProvenance = DocumentProvenance;
export type ScoreRights = DocumentRights;

/** Stable catalogue identity. The musical payload remains the pure Score model;
 * records, revisions and listings can move independently around a future
 * private/submission/public workflow. */
export interface ScoreRecord {
  id: string;
  controller: PrincipalRef;
  provenance: ScoreProvenance;
  rights: ScoreRights;
  /** A derivative gets a new record id while retaining its lineage. */
  derivedFrom?: { scoreId: string; revisionId?: string };
}

/** A revision is immutable once submitted or published. New edits create the
 * next revision under the same record id. */
export interface ScoreRevisionRef {
  id: string;
  scoreId: string;
  number: number;
  payloadVersion: number;
}

export type ScoreCollection = 'private' | 'submissions' | 'published' | 'bundled';
export type ScoreListingStatus = 'active' | 'pending' | 'rejected' | 'withdrawn' | 'retired';

/** Promotion adds or changes a listing; it never copies/rekeys the score. */
export interface ScoreListing {
  collection: ScoreCollection;
  scoreId: string;
  revisionId: string;
  status: ScoreListingStatus;
}

export interface ScoreSummary {
  title: string;
  composer?: string;
  style?: string;
  musicKey?: string;
  measures: number;
  hasMelody: boolean;
  /** Carries at least one sung syllable. Optional and written only when true,
   * matching the corpus manifests' `lyrics` flag: absent means "no lyrics",
   * and `summariesMatchPayloads` in bundledScores.test.ts pins every entry
   * against `scoreHasLyrics` of the real document so the hand-written summary
   * cannot drift from the music. */
  hasLyrics?: boolean;
}

export interface ScoreCatalogEntry {
  record: ScoreRecord;
  revision: ScoreRevisionRef;
  /** Published versions this entry replaces. Unknown IDs may come from a
   * newer app build and must never be offered a downgrade as an update. */
  previousRevisionIds?: readonly string[];
  listing: ScoreListing;
  summary: ScoreSummary;
  /** The day the score was first listed (YYYY-MM-DD): the home page's
   * Recently added shelf orders by it. A new revision keeps the date. */
  addedAt: string;
  asset: { kind: 'bundled'; file: string };
}

export function scoreCatalogKey(entry: ScoreCatalogEntry): string {
  return `score:${entry.record.id}@${entry.revision.id}`;
}
